import { useParams, Link, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import { useBlog } from "@/lib/blog";
import { CONTACT } from "@/lib/site";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import NewsletterSignup from "@/components/site/NewsletterSignup";
import ShareButtons from "@/components/site/ShareButtons";
import useSeo from "@/lib/useSeo";
import { useJsonLd, buildBreadcrumbSchema } from "@/lib/useJsonLd";
import { useT, useLang } from "@/lib/i18n";
import ArticleYogaOncologico from "@/pages/blog/ArticleYogaOncologico";
import ArticleFatigaCronica from "@/pages/blog/ArticleFatigaCronica";
import ArticleDiagnosticoCancer from "@/pages/blog/ArticleDiagnosticoCancer";
import ArticleVidaDespuesCancer from "@/pages/blog/ArticleVidaDespuesCancer";
import ArticleEvidenciaCientifica from "@/pages/blog/ArticleEvidenciaCientifica";
import ArticleLenguajeBelico from "@/pages/blog/ArticleLenguajeBelico";
import ArticleAutocompasion from "@/pages/blog/ArticleAutocompasion";
import ArticleDiaFibromialgia from "@/pages/blog/ArticleDiaFibromialgia";
import ArticleDerechoOlvidoOncologico from "@/pages/blog/ArticleDerechoOlvidoOncologico";
import ArticleSiFueCancer from "@/pages/blog/ArticleSiFueCancer";
import ArticleAnsiedadDespuesCancer from "@/pages/blog/ArticleAnsiedadDespuesCancer";
import ArticleAnsiedadQuimioterapia from "@/pages/blog/ArticleAnsiedadQuimioterapia";
import ArticleAnsiedadHormonoterapia from "@/pages/blog/ArticleAnsiedadHormonoterapia";
import ArticleAtaquesPanicoDespuesCancer from "@/pages/blog/ArticleAtaquesPanicoDespuesCancer";

const articleComponents = {
  "yoga-oncologico-online-como-funciona": ArticleYogaOncologico,
  "fatiga-cronica-despues-cancer-mama": ArticleFatigaCronica,
  "diagnostico-cancer-por-donde-empezar": ArticleDiagnosticoCancer,
  "recuperar-confianza-cuerpo-despues-cancer": ArticleVidaDespuesCancer,
  "evidencia-cientifica-yoga-oncologico-mindfulness": ArticleEvidenciaCientifica,
  "lenguaje-belico-cancer": ArticleLenguajeBelico,
  "autocompasion-cancer": ArticleAutocompasion,
  "dia-internacional-fibromialgia-fatiga-cronica": ArticleDiaFibromialgia,
  "derecho-olvido-oncologico": ArticleDerechoOlvidoOncologico,
  "si-fue-cancer-carcinoma-ductal-in-situ": ArticleSiFueCancer,
  "ansiedad-despues-cancer": ArticleAnsiedadDespuesCancer,
  "ansiedad-quimioterapia": ArticleAnsiedadQuimioterapia,
  "ansiedad-hormonoterapia": ArticleAnsiedadHormonoterapia,
  "ataques-panico-despues-cancer": ArticleAtaquesPanicoDespuesCancer,
};

const BlogArticle = () => {
  const t = useT();
  const lang = useLang();
  const { slug } = useParams();
  const { getArticle } = useBlog();
  const article = getArticle(slug);

  const locale = lang === "ca" ? "ca-ES" : "es-ES";
  const formatDate = (iso) => new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });

  useSeo({
    title: article ? article.metaTitle || article.title : t("article.notFound"),
    description: article?.description || "",
    path: `/blog/${slug}`,
    image: article?.coverImage,
    imageAlt: article?.title,
    article: article
      ? {
          publishedTime: article.publishedAt,
          author: "Marta Montardit",
          section: article.category,
          tags: article.keywords,
        }
      : undefined,
  });

  useJsonLd(
    `breadcrumb-${slug}`,
    article
      ? buildBreadcrumbSchema([
          { name: "Inicio", url: "https://metodo-tao.com/" },
          { name: "Blog", url: "https://metodo-tao.com/blog" },
          { name: article.title, url: `https://metodo-tao.com/blog/${slug}` },
        ])
      : null
  );

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const ArticleContent = articleComponents[slug];

  return (
    <div className="bg-ivory">
      <section className="pt-12 md:pt-20 pb-12" data-testid={`article-${slug}`}>
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <Reveal>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-charcoal/55 hover:text-terracotta transition-colors mb-10"
            >
              <ArrowLeft size={12} /> {t("article.back")}
            </Link>
            <div>
              <span className="inline-block text-[10px] tracking-[0.3em] uppercase text-sage mb-5">
                {article.category}
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1.05]">
              {article.title}
            </h1>
            <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/70 font-light">
              {article.description}
            </p>
            <div className="mt-8 flex items-center gap-6 text-[11px] tracking-[0.2em] uppercase text-charcoal/45">
              <span className="inline-flex items-center gap-2">
                <Calendar size={13} /> {formatDate(article.publishedAt)}
              </span>
              <span className="inline-flex items-center gap-2">
                <Clock size={13} /> {article.readingMinutes} {t("article.minRead")}
              </span>
              <span className="hidden md:inline">{t("article.byAuthor")}</span>
            </div>
            <div className="mt-7">
              <ShareButtons title={article.title} url={`/blog/${slug}`} compact />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-12 md:pb-16">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="aspect-[16/10] md:aspect-[16/9] rounded-3xl overflow-hidden bg-sand">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <article className="pb-20">
        <div className="max-w-[760px] mx-auto px-6 md:px-12">
          {ArticleContent ? <ArticleContent /> : null}

          <aside className="my-16 bg-charcoal text-ivory rounded-3xl p-8 md:p-10 grain relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-terracotta/20 blur-3xl" />
            <p className="relative text-[11px] tracking-[0.3em] uppercase text-terracotta mb-4">
              {t("article.midCta.eyebrow")}
            </p>
            <h3 className="relative font-serif text-3xl md:text-4xl leading-tight">
              {t("article.midCta.title1")} <span className="italic text-terracotta">{t("article.midCta.title2")}</span>?
            </h3>
            <p className="relative mt-4 text-ivory/75 text-[15px] leading-relaxed max-w-md">
              {t("article.midCta.body")}
            </p>
            <div className="relative mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/sesion-gratuita"
                className="inline-flex items-center justify-center gap-2 bg-terracotta text-charcoal rounded-full px-6 py-3 text-sm font-medium hover:bg-ivory transition-colors"
              >
                {t("article.midCta.btn")} <ArrowRight size={15} />
              </Link>
              <a
                href={CONTACT.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-ivory/30 text-ivory rounded-full px-6 py-3 text-sm font-medium hover:bg-ivory hover:text-charcoal transition-colors"
              >
                <MessageCircle size={15} /> WhatsApp
              </a>
            </div>
          </aside>

          <div className="mt-20 pt-10 border-t border-sand">
            <div className="flex items-start gap-5">
              <img
                src="https://customer-assets.emergentagent.com/job_acompanamiento-tao/artifacts/yoox5k3i_WhatsApp%20Image%202026-01-12%20at%2009.48.06.jpeg"
                alt="Marta Montardit"
                className="w-20 h-20 rounded-full object-cover flex-shrink-0"
                loading="lazy"
                decoding="async"
              />
              <div>
                <p className="text-[11px] tracking-[0.3em] uppercase text-charcoal/55">{t("article.author.eyebrow")}</p>
                <h4 className="font-serif text-2xl text-charcoal mt-1">Marta Montardit</h4>
                <p className="mt-3 text-charcoal/70 text-[15px] leading-relaxed">
                  {t("article.author.bio")}
                </p>
                <Link
                  to="/mi-historia"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-charcoal hover:text-terracotta transition-colors border-b border-terracotta pb-0.5"
                >
                  {t("article.author.readStory")} <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <ShareButtons title={article.title} url={`/blog/${slug}`} />
          </div>

          <div className="mt-16">
            <NewsletterSignup variant="light" source={`article-${slug}`} />
          </div>
        </div>
      </article>

      <SectionCTA />
    </div>
  );
};

export default BlogArticle;
